import { onMounted } from '@nuxtjs/composition-api';

import { useNostoStore } from '~/diptyqueTheme/stores/nosto';

type ScriptLoadOptions = {
  /**
   * Indicates the position of the script, default is "body"
   */
  position?: 'head' | 'body';
  /**
   * Indicates the attributes of the script element
   */
  attributes?: Record<string, string>;
};

const defaultAttributes = { 'nosto-client-script': '' };

function defaultScriptLoader(scriptSrc: string, options?: ScriptLoadOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = scriptSrc;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject();
    Object.entries(options?.attributes ?? {}).forEach(([k, v]) => script.setAttribute(k, v));
    if (options?.position === 'head') {
      document.head.appendChild(script);
    } else {
      document.body.appendChild(script);
    }
  });
}

export default function useNostoClientScript() {
  const { getClientScriptLoaded, setClientScriptLoaded, getAccountId } = useNostoStore();

  const loadClientScript = async () => {
    const { init, isNostoLoaded, nostojs } = await import('@nosto/nosto-js');

    function scriptOnload() {
      setClientScriptLoaded();
    }

    if (getClientScriptLoaded()) {
      nostojs(scriptOnload);
      return;
    }

    async function initClientScript() {
      await init({
        merchantId: getAccountId(),
        options: {
          attributes: defaultAttributes,
          position: 'head'
        },
        scriptLoader: defaultScriptLoader
      });
      scriptOnload();
    }

    if (!isNostoLoaded()) {
      await initClientScript();
    }
  };

  onMounted(() => {
    loadClientScript();
  });
}
